import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import { Star, Check, Loader2, ExternalLink } from 'lucide-react';

/**
 * Iter 193 — Featured Business Membership upgrade card.
 *
 * Renders one of three states:
 *   1. Loading skeleton while we fetch /api/business/featured/status
 *   2. "Upgrade to Featured" CTA when isFeatured === false
 *   3. "Featured active" panel with a Manage button when isFeatured === true
 *
 * The card is purely informational + a Stripe Checkout redirect — no public
 * surface yet (carousel/badge/video player deferred to Phase 2). Cancelling
 * goes through the Stripe customer portal which we open via /portal.
 */
const FeaturedUpgradeCard = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const token = (typeof window !== 'undefined' && localStorage.getItem('access_token')) || '';
  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/business/featured/status`, { headers: authHeader });
        if (!cancelled) setStatus(r.data);
      } catch (e) {
        if (!cancelled) setStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleUpgrade = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(
        `${API}/business/featured/checkout`,
        { originUrl: window.location.origin },
        { headers: authHeader },
      );
      if (r.data?.checkoutUrl) window.location.assign(r.data.checkoutUrl);
    } catch (e) {
      const detail = e.response?.data?.detail || t('featured.error', 'Could not start checkout. Please try again.');
      setError(typeof detail === 'string' ? detail : t('featured.error', 'Could not start checkout. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const handleManage = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(
        `${API}/business/featured/portal`,
        { originUrl: window.location.origin },
        { headers: authHeader },
      );
      if (r.data?.portalUrl) window.location.assign(r.data.portalUrl);
    } catch (e) {
      const detail = e.response?.data?.detail || t('featured.portalError', 'Could not open management portal.');
      setError(typeof detail === 'string' ? detail : t('featured.portalError', 'Could not open management portal.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="featured-card-loading">
        <div className="h-5 w-44 bg-amber-200/60 rounded animate-pulse" />
        <div className="mt-3 h-3 w-72 bg-amber-200/40 rounded animate-pulse" />
      </div>
    );
  }

  if (!status) return null;
  const isFeatured = !!status.isFeatured;
  const limits = status.limits || {};
  const expires = status.expiresAt ? new Date(status.expiresAt).toLocaleDateString() : null;

  return (
    <div
      className={`rounded-2xl border-2 p-5 shadow-sm ${
        isFeatured
          ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50'
          : 'border-amber-300 bg-amber-50'
      }`}
      data-testid="featured-card"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-200 text-amber-700">
          <Star className="h-5 w-5" fill={isFeatured ? 'currentColor' : 'none'} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700">
            {isFeatured
              ? t('featured.activeBadge', 'Featured Business · Active')
              : t('featured.upgradeBadge', 'Optional upgrade')}
          </p>
          <h3 className="text-lg font-bold text-amber-900 mt-0.5">
            ⭐ {t('featured.title', 'Featured Business Membership')}
          </h3>

          {!isFeatured && (
            <>
              <p className="text-sm text-amber-900/90 mt-1.5 leading-relaxed">
                {t(
                  'featured.pitch',
                  'Unlock higher limits and showcase your listing with videos. Cancel anytime — your existing photos, events and offers stay intact even after cancelling.',
                )}
              </p>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-amber-900">
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{t('featured.benefitPhotos', 'Up to 15 photos')}</li>
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{t('featured.benefitEvents', 'Up to 15 active events')}</li>
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{t('featured.benefitOffers', 'Up to 15 active offers')}</li>
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{t('featured.benefitVideos', '3 video links (YouTube · Vimeo · Facebook)')}</li>
              </ul>
              <div className="mt-4 flex items-end justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-2xl font-extrabold text-amber-900 leading-none">€49 <span className="text-sm font-medium text-amber-700">/ {t('featured.year', 'year')}</span></p>
                  <p className="text-xs text-amber-700 mt-1">{t('featured.vatNote', 'IVA incluido · Cancel anytime')}</p>
                </div>
                <button
                  type="button"
                  onClick={handleUpgrade}
                  disabled={busy || status.configured === false}
                  className="px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm shadow disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  data-testid="featured-upgrade-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
                  {t('featured.upgradeCta', 'Upgrade to Featured')}
                </button>
              </div>
              {status.configured === false && (
                <p className="mt-2 text-xs text-amber-700 italic" data-testid="featured-not-configured">
                  {t('featured.notConfigured', 'Coming soon — the upgrade flow is being finalised.')}
                </p>
              )}
            </>
          )}

          {isFeatured && (
            <>
              <p className="text-sm text-amber-900/90 mt-1.5 leading-relaxed">
                {t(
                  'featured.activeBody',
                  'Your membership is active. You can now use the higher limits below.',
                )}
              </p>
              <ul className="mt-3 grid grid-cols-2 gap-1.5 text-sm text-amber-900">
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{limits.maxPhotos || 15} {t('featured.photos', 'photos')}</li>
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{limits.maxActiveEvents || 15} {t('featured.events', 'events')}</li>
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{limits.maxActiveOffers || 15} {t('featured.offers', 'offers')}</li>
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{limits.maxVideoLinks || 3} {t('featured.videos', 'video links')}</li>
              </ul>
              {expires && (
                <p className="mt-3 text-xs text-amber-700">
                  {status.cancelAtPeriodEnd
                    ? t('featured.renewsCancelled', 'Cancels on {{date}} — limits revert to free tier (existing content stays).', { date: expires })
                    : t('featured.renewsOn', 'Renews automatically on {{date}}.', { date: expires })}
                </p>
              )}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleManage}
                  disabled={busy}
                  className="px-5 py-2.5 rounded-lg bg-white border border-amber-400 text-amber-800 hover:bg-amber-100 font-semibold text-sm shadow-sm disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="featured-manage-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                  {t('featured.manageCta', 'Manage subscription')}
                </button>
              </div>
            </>
          )}

          {error && (
            <p className="mt-3 text-sm text-red-700" data-testid="featured-error">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedUpgradeCard;
